import React, { useState, useEffect } from 'react';
import { Search, Plus, MoreVertical, Eye, Edit, Trash2, Copy, Play, Pause, BarChart3, Users, Calendar, Filter } from 'lucide-react';

import { PageLoading, SectionLoading, ErrorState, LoadingButton } from '../components/StandardizedLoading';
import { ErrorHandler, SuccessHandler, createAsyncHandler } from '../utils/errorHandler';
import {
  getSurveys,
  createSurvey as apiCreateSurvey,
  activateSurvey,
  pauseSurvey,
  completeSurvey,
  duplicateSurvey,
  deleteSurvey as apiDeleteSurvey,
  getSurveyById
} from '../services/api';

const SurveyManagement = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);

  const surveysPerPage = 10;

  useEffect(() => {
    fetchSurveys();
  }, [currentPage, searchTerm, filterStatus]);

  const mapStatus = (isActive, isPublished) => {
    if (isActive && isPublished) return 'active';
    if (!isPublished && isActive) return 'draft';
    if (!isActive && isPublished) return 'paused';
    return 'completed';
  };

  const mapSurveyFromApi = (s) => ({
    id: s.id,
    title: s.title,
    description: s.description || '',
    status: mapStatus(Boolean(s.isActive), Boolean(s.isPublished)),
    createdDate: s.createdAt,
    responses: s.responseCount || 0,
    targetResponses: s.maxResponses || 0,
    reward: s.pointsReward || 0,
    category: s.category || 'General',
    duration: s.estimatedDuration ? `${s.estimatedDuration} minutes` : 'N/A',
    // questions count will be loaded in detail view
  });

  const fetchSurveys = createAsyncHandler(
    async () => {
      const serverStatus = filterStatus === 'active' ? 'active' : filterStatus === 'draft' ? 'draft' : '';
      const params = {
        page: currentPage,
        limit: surveysPerPage,
        search: searchTerm,
        ...(serverStatus && { status: serverStatus })
      };
      const result = await getSurveys(params);
      const list = (result?.data?.surveys || []).map(mapSurveyFromApi);
      setSurveys(list);
      setTotalPages(result?.data?.pagination?.totalPages || 1);
    },
    {
      loadingState: setLoading,
      errorContext: 'fetching surveys',
      showErrorToast: true
    }
  );

  const handleViewDetails = async (surveyId) => {
    try {
      setLoading(true);
      const result = await getSurveyById(surveyId);
      const s = result?.data?.survey;
      if (!s) throw new Error('Survey not found');
      const mapped = {
        id: s.id,
        title: s.title,
        description: s.description || '',
        status: mapStatus(Boolean(s.isActive), Boolean(s.isPublished)),
        createdDate: s.createdAt,
        responses: Array.isArray(s.SurveyResponses) ? s.SurveyResponses.length : 0,
        targetResponses: s.maxResponses || 0,
        reward: s.pointsReward || 0,
        category: s.category || 'General',
        duration: s.estimatedDuration ? `${s.estimatedDuration} minutes` : 'N/A',
        questions: Array.isArray(s.questions) ? s.questions.length : 0,
      };
      setSelectedSurvey(mapped);
      setShowSurveyModal(true);
    } catch (err) {
      console.error('Error loading survey details:', err);
      ErrorHandler.handle(err, 'loading survey details');
    } finally {
      setLoading(false);
    }
  };

  const handleSurveyAction = createAsyncHandler(
    async (action, surveyId) => {
      switch (action) {
        case 'activate':
          await activateSurvey(surveyId);
          SuccessHandler.show('Survey activated successfully');
          break;
        case 'pause':
          await pauseSurvey(surveyId);
          SuccessHandler.show('Survey paused successfully');
          break;
        case 'complete':
          await completeSurvey(surveyId);
          SuccessHandler.show('Survey completed successfully');
          break;
        case 'duplicate':
          await duplicateSurvey(surveyId);
          SuccessHandler.show('Survey duplicated successfully');
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this survey?')) {
            try {
              await apiDeleteSurvey(surveyId);
              SuccessHandler.show('Survey deleted successfully');
            } catch (error) {
              // Check if error is due to existing responses
              if (error.response?.status === 400 && error.response?.data?.responseCount) {
                const responseCount = error.response.data.responseCount;
                const forceDelete = window.confirm(
                  `This survey has ${responseCount} response(s). Do you want to delete the survey AND all its responses?\n\n⚠️ This action cannot be undone!`
                );
                if (forceDelete) {
                  await apiDeleteSurvey(surveyId, true); // Force delete
                  SuccessHandler.show(`Survey and ${responseCount} responses deleted successfully`);
                }
              } else {
                throw error; // Re-throw other errors
              }
            }
          }
          break;
        default:
          break;
      }
      await fetchSurveys();
      setActionDropdown(null);
    },
    {
      errorContext: 'updating survey',
      showErrorToast: true
    }
  );

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      paused: 'bg-orange-100 text-orange-800',
      completed: 'bg-blue-100 text-blue-800',
      archived: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'
        }`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getProgressBar = (responses, target) => {
    const percentage = target > 0 ? Math.min((responses / target) * 100, 100) : 0;
    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-primary-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    );
  };

  const SurveyModal = ({ survey, onClose }) => {
    if (!survey) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
        <div className="relative top-4 sm:top-20 mx-auto p-4 sm:p-6 border border-gray-200 w-full max-w-2xl shadow-lg rounded-lg bg-white admin-modal">
          <div className="mt-3">
            <div className="flex items-center justify-between mb-3 gap-4">
              <h3 className="text-base font-medium text-gray-900 min-w-0 truncate">Survey Details</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1 text-lg"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="admin-spacing-xs">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Title</label>
                  <p className="text-xs text-gray-900 break-words">{survey.title}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Description</label>
                  <p className="text-xs text-gray-900 break-words">{survey.description}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Category</label>
                  <p className="text-xs text-gray-900">{survey.category}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Status</label>
                  {getStatusBadge(survey.status)}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Duration</label>
                  <p className="text-xs text-gray-900">{survey.duration}</p>
                </div>
              </div>

              <div className="admin-spacing-xs">
                <div>
                  <label className="block text-xs font-medium text-gray-700">Questions</label>
                  <p className="text-xs text-gray-900">{survey.questions ?? 0} questions</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Reward</label>
                  <p className="text-xs text-gray-900">${survey.reward}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Created Date</label>
                  <p className="text-xs text-gray-900">{new Date(survey.createdDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700">Progress</label>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span>{survey.responses} responses</span>
                      <span>{survey.targetResponses} target</span>
                    </div>
                    {getProgressBar(survey.responses, survey.targetResponses)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-2 mt-4">
              <button
                onClick={onClose}
                className="px-3 py-1.5 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-xs w-full sm:w-auto"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const CreateSurveyModal = ({ onClose }) => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
      title: '',
      description: '',
      category: '',
      reward: '',
      targetResponses: '',
      duration: ''
    });
    const [surveyType, setSurveyType] = useState(''); // 'link' or 'manual'
    const [externalLink, setExternalLink] = useState('');
    const [questions, setQuestions] = useState([]);
    const [enterAnim, setEnterAnim] = useState(false);

    useEffect(() => {
      const t = setTimeout(() => setEnterAnim(true), 10);
      return () => clearTimeout(t);
    }, []);

    const QUESTION_TYPES = [
      { key: 'mcq_single', label: 'MCQ Single', icon: '○' },
      { key: 'mcq_multi', label: 'MCQ Multi', icon: '☐' },
      { key: 'short_text', label: 'Short Text', icon: '✎' },
      { key: 'long_text', label: 'Long Text', icon: '¶' },
      { key: 'number', label: 'Number', icon: '#' },
      { key: 'rating', label: 'Rating', icon: '★' },
      { key: 'dropdown', label: 'Dropdown', icon: '▼' },
      { key: 'date', label: 'Date', icon: '📅' },
      { key: 'fill_in_blank', label: 'Fill in Blank', icon: '___' },
      { key: 'email', label: 'Email', icon: '@' },
      { key: 'phone', label: 'Phone', icon: '☎' },
      { key: 'matrix', label: 'Matrix', icon: '▦' }
    ];

    const addQuestion = (type) => {
      const base = { id: Date.now(), type, question: '', required: false };
      if (['mcq_single', 'mcq_multi', 'dropdown'].includes(type)) {
        base.options = ['Option 1', 'Option 2'];
      }
      if (type === 'rating') {
        base.scale = 5;
      }
      if (type === 'matrix') {
        base.rows = ['Row 1'];
        base.columns = ['Column 1', 'Column 2'];
      }
      setQuestions(prev => [...prev, base]);
    };

    const updateQuestion = (id, patch) => {
      setQuestions(prev => prev.map(q => (q.id === id ? { ...q, ...patch } : q)));
    };

    const removeQuestion = (id) => {
      setQuestions(prev => prev.filter(q => q.id !== id));
    };

    const handleNextStep = (e) => {
      e.preventDefault();
      setStep(2);
    };

    const handleSelectSurveyType = (type) => {
      setSurveyType(type);
      setStep(3);
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        const estimatedDuration = parseInt(String(formData.duration).replace(/[^0-9]/g, ''), 10) || 5;
        const payload = {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          questions: surveyType === 'manual' ? questions.map(({ id, ...rest }) => rest) : [],
          pointsReward: Number(formData.reward) || 0,
          estimatedDuration,
          maxResponses: Number(formData.targetResponses) || 0,
          isActive: true,
          isPublished: false,
          surveyType: surveyType,
          externalSurveyUrl: surveyType === 'link' ? externalLink : null
        };
        await apiCreateSurvey(payload);
        SuccessHandler.show('Survey created successfully');
        fetchSurveys();
        onClose();
      } catch (error) {
        console.error('Error creating survey:', error);
        ErrorHandler.handle(error, 'creating survey');
      }
    };

    const getModalWidth = () => {
      if (step === 1) return 'max-w-2xl';
      if (step === 2) return 'max-w-4xl';
      return 'max-w-4xl';
    };

    return (
      <div className={`fixed inset-0 bg-gray-600 overflow-y-auto h-full w-full z-50 p-4 transition-opacity duration-200 flex items-start justify-center ${enterAnim ? 'bg-opacity-50' : 'bg-opacity-0'}`}>
        <div className={`relative mt-12 mb-8 p-6 border border-gray-200 w-full ${getModalWidth()} shadow-2xl rounded-xl bg-white transform transition-all duration-300 ${enterAnim ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-[0.99]'}`}>

          {/* Header with Steps */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Create New Survey</h3>
              <div className="flex items-center gap-3 mt-2">
                <div className={`flex items-center gap-2 text-sm ${step === 1 ? 'text-blue-600 font-medium' : step > 1 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === 1 ? 'bg-blue-600 text-white' : step > 1 ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>{step > 1 ? '✓' : '1'}</span>
                  Details
                </div>
                <div className="w-6 h-px bg-gray-300"></div>
                <div className={`flex items-center gap-2 text-sm ${step === 2 ? 'text-blue-600 font-medium' : step > 2 ? 'text-green-600' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === 2 ? 'bg-blue-600 text-white' : step > 2 ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>{step > 2 ? '✓' : '2'}</span>
                  Survey Type
                </div>
                <div className="w-6 h-px bg-gray-300"></div>
                <div className={`flex items-center gap-2 text-sm ${step === 3 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step === 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>3</span>
                  {surveyType === 'link' ? 'Survey Link' : 'Questions'}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-2 transition-colors text-2xl leading-none">×</button>
          </div>

          {/* Step 1: Details */}
          {step === 1 && (
            <form onSubmit={handleNextStep} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Survey Title *</label>
                <input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" placeholder="Enter survey title" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea required value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none" placeholder="Describe the survey purpose" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select required value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[50px]">
                    <option value="">Select Category</option>
                    <option value="Customer Service">Customer Service</option>
                    <option value="Product Development">Product Development</option>
                    <option value="Market Research">Market Research</option>
                    <option value="User Experience">User Experience</option>
                    <option value="General Feedback">General Feedback</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reward Points *</label>
                  <input type="number" required min="0" value={formData.reward} onChange={(e) => setFormData({ ...formData, reward: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" placeholder="e.g. 50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Target Responses *</label>
                  <input type="number" required min="1" value={formData.targetResponses} onChange={(e) => setFormData({ ...formData, targetResponses: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" placeholder="e.g. 100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duration *</label>
                  <select required value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-[50px]">
                    <option value="">Select Duration</option>
                    <option value="1-5 minutes">1-5 minutes</option>
                    <option value="5-10 minutes">5-10 minutes</option>
                    <option value="10-15 minutes">10-15 minutes</option>
                    <option value="15-20 minutes">15-20 minutes</option>
                    <option value="20+ minutes">20+ minutes</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">Cancel</button>
                <button type="submit" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shadow-sm">Next: Choose Survey Type →</button>
              </div>
            </form>
          )}

          {/* Step 2: Survey Type Selection */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="text-center mb-8">
                <h4 className="text-2xl font-bold text-gray-800 mb-3">How would you like to create your survey?</h4>
                <p className="text-base text-gray-500">Choose between using an external survey link or building questions manually</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Link-Based Survey Card */}
                <button
                  type="button"
                  onClick={() => handleSelectSurveyType('link')}
                  className={`group relative p-8 border-2 rounded-2xl text-left transition-all duration-200 hover:shadow-xl min-h-[220px] ${surveyType === 'link'
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300 bg-white hover:bg-purple-50/50'
                    }`}
                >
                  <div className="flex items-start gap-5">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors flex-shrink-0 ${surveyType === 'link' ? 'bg-purple-500' : 'bg-purple-100 group-hover:bg-purple-200'
                      }`}>
                      <svg className={`w-8 h-8 ${surveyType === 'link' ? 'text-white' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-bold text-gray-900 mb-2">Link-Based Survey</h5>
                      <p className="text-sm text-gray-500 leading-relaxed">Use an external survey URL (Google Forms, Typeform, SurveyMonkey, etc.)</p>
                      <ul className="mt-4 space-y-2">
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Quick setup with existing surveys
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Redirects users to external platform
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>

                {/* Manual Survey Card */}
                <button
                  type="button"
                  onClick={() => handleSelectSurveyType('manual')}
                  className={`group relative p-8 border-2 rounded-2xl text-left transition-all duration-200 hover:shadow-xl min-h-[220px] ${surveyType === 'manual'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300 bg-white hover:bg-blue-50/50'
                    }`}
                >
                  <div className="flex items-start gap-5">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors flex-shrink-0 ${surveyType === 'manual' ? 'bg-blue-500' : 'bg-blue-100 group-hover:bg-blue-200'
                      }`}>
                      <svg className={`w-8 h-8 ${surveyType === 'manual' ? 'text-white' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-lg font-bold text-gray-900 mb-2">Manual Survey Creation</h5>
                      <p className="text-sm text-gray-500 leading-relaxed">Build your survey questions directly in the platform</p>
                      <ul className="mt-4 space-y-2">
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Full control over questions
                        </li>
                        <li className="flex items-center gap-2 text-sm text-gray-600">
                          <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          In-app response collection
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              <div className="flex justify-between gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setStep(1)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">← Back to Details</button>
                <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">Cancel</button>
              </div>
            </div>
          )}

          {/* Step 3: Link-Based Survey */}
          {step === 3 && surveyType === 'link' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500 flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">External Survey Link</h4>
                    <p className="text-xs text-gray-500">Enter the URL where respondents will be redirected</p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Survey URL *</label>
                  <input
                    type="url"
                    required
                    value={externalLink}
                    onChange={(e) => setExternalLink(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    placeholder="https://forms.google.com/your-survey-link"
                  />
                  <p className="mt-2 text-xs text-gray-500">Supported: Google Forms, Typeform, SurveyMonkey, Microsoft Forms, and other survey platforms</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Survey Summary</h5>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-gray-500">Title:</span> <span className="font-medium text-gray-900">{formData.title}</span></div>
                  <div><span className="text-gray-500">Category:</span> <span className="font-medium text-gray-900">{formData.category}</span></div>
                  <div><span className="text-gray-500">Reward:</span> <span className="font-medium text-green-600">{formData.reward} points</span></div>
                  <div><span className="text-gray-500">Target:</span> <span className="font-medium text-gray-900">{formData.targetResponses} responses</span></div>
                </div>
              </div>

              <div className="flex justify-between gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setStep(2)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">← Back</button>
                <div className="flex gap-3">
                  <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">Cancel</button>
                  <button type="submit" className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium shadow-sm">Create Survey</button>
                </div>
              </div>
            </form>
          )}

          {/* Step 3: Manual Questions */}
          {step === 3 && surveyType === 'manual' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-800">Add Questions</h4>
                  <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">{questions.length} question(s)</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  {QUESTION_TYPES.map(t => (
                    <button type="button" key={t.key} onClick={() => addQuestion(t.key)} className="px-3 py-1.5 text-xs bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all shadow-sm flex items-center gap-1">
                      <span className="text-base leading-none">{t.icon}</span> {t.label}
                    </button>
                  ))}
                </div>
                {questions.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-lg bg-white">
                    <p className="text-gray-500 text-sm">Click a question type above to add questions</p>
                    <p className="text-gray-400 text-xs mt-1">You can add multiple question types to create a rich survey</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                    {questions.map((q, idx) => (
                      <div key={q.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            Q{idx + 1}: {QUESTION_TYPES.find(t => t.key === q.type)?.icon} {QUESTION_TYPES.find(t => t.key === q.type)?.label}
                          </span>
                          <button type="button" onClick={() => removeQuestion(q.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Remove</button>
                        </div>
                        <input type="text" value={q.question} onChange={(e) => updateQuestion(q.id, { question: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm mb-3" placeholder="Enter your question" />

                        {/* MCQ / Dropdown options */}
                        {['mcq_single', 'mcq_multi', 'dropdown'].includes(q.type) && (
                          <div className="space-y-2 mb-3">
                            {(q.options || []).map((opt, oidx) => (
                              <div key={oidx} className="flex gap-2">
                                <span className="flex items-center justify-center w-6 text-xs text-gray-400">
                                  {q.type === 'mcq_single' ? '○' : q.type === 'mcq_multi' ? '☐' : `${oidx + 1}.`}
                                </span>
                                <input type="text" value={opt} onChange={(e) => { const options = [...q.options]; options[oidx] = e.target.value; updateQuestion(q.id, { options }); }} className="flex-1 px-3 py-2 border border-gray-200 rounded text-sm" placeholder={`Option ${oidx + 1}`} />
                                {q.options.length > 1 && <button type="button" onClick={() => updateQuestion(q.id, { options: q.options.filter((_, i) => i !== oidx) })} className="px-2 text-gray-400 hover:text-red-500 text-lg">×</button>}
                              </div>
                            ))}
                            <button type="button" onClick={() => updateQuestion(q.id, { options: [...(q.options || []), ''] })} className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Add Option</button>
                          </div>
                        )}

                        {/* Rating scale selector */}
                        {q.type === 'rating' && (
                          <div className="mb-3">
                            <label className="text-xs text-gray-600 mb-1 block">Rating Scale</label>
                            <select value={q.scale || 5} onChange={(e) => updateQuestion(q.id, { scale: parseInt(e.target.value) })} className="px-3 py-2 border border-gray-200 rounded text-sm">
                              {[3, 5, 7, 10].map(n => <option key={n} value={n}>1 to {n}</option>)}
                            </select>
                            <div className="flex gap-1 mt-2">
                              {Array.from({ length: q.scale || 5 }, (_, i) => (
                                <span key={i} className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-gray-500">{i + 1}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Matrix rows/columns */}
                        {q.type === 'matrix' && (
                          <div className="mb-3 space-y-3">
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Rows</label>
                              {(q.rows || []).map((row, ri) => (
                                <div key={ri} className="flex gap-2 mb-1">
                                  <input type="text" value={row} onChange={(e) => { const rows = [...(q.rows || [])]; rows[ri] = e.target.value; updateQuestion(q.id, { rows }); }} className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-sm" placeholder={`Row ${ri + 1}`} />
                                  {(q.rows || []).length > 1 && <button type="button" onClick={() => updateQuestion(q.id, { rows: q.rows.filter((_, i) => i !== ri) })} className="px-2 text-gray-400 hover:text-red-500">×</button>}
                                </div>
                              ))}
                              <button type="button" onClick={() => updateQuestion(q.id, { rows: [...(q.rows || []), ''] })} className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Add Row</button>
                            </div>
                            <div>
                              <label className="text-xs text-gray-600 mb-1 block">Columns</label>
                              {(q.columns || []).map((col, ci) => (
                                <div key={ci} className="flex gap-2 mb-1">
                                  <input type="text" value={col} onChange={(e) => { const columns = [...(q.columns || [])]; columns[ci] = e.target.value; updateQuestion(q.id, { columns }); }} className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-sm" placeholder={`Column ${ci + 1}`} />
                                  {(q.columns || []).length > 1 && <button type="button" onClick={() => updateQuestion(q.id, { columns: q.columns.filter((_, i) => i !== ci) })} className="px-2 text-gray-400 hover:text-red-500">×</button>}
                                </div>
                              ))}
                              <button type="button" onClick={() => updateQuestion(q.id, { columns: [...(q.columns || []), ''] })} className="text-xs text-blue-600 hover:text-blue-800 font-medium">+ Add Column</button>
                            </div>
                          </div>
                        )}

                        {/* Type-specific preview hints */}
                        {['short_text', 'fill_in_blank'].includes(q.type) && (
                          <div className="mb-3 px-3 py-2 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-400">Short text answer field</div>
                        )}
                        {q.type === 'long_text' && (
                          <div className="mb-3 px-3 py-4 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-400">Long text / paragraph answer field</div>
                        )}
                        {q.type === 'number' && (
                          <div className="mb-3 px-3 py-2 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-400">Numeric input field</div>
                        )}
                        {q.type === 'date' && (
                          <div className="mb-3 px-3 py-2 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-400">📅 Date picker field</div>
                        )}
                        {q.type === 'email' && (
                          <div className="mb-3 px-3 py-2 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-400">@ Email input field</div>
                        )}
                        {q.type === 'phone' && (
                          <div className="mb-3 px-3 py-2 bg-gray-50 rounded border border-dashed border-gray-200 text-xs text-gray-400">☎ Phone number input field</div>
                        )}

                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <input type="checkbox" checked={q.required} onChange={(e) => updateQuestion(q.id, { required: e.target.checked })} className="rounded" />
                          Required question
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-between gap-3 pt-4 border-t border-gray-200">
                <button type="button" onClick={() => setStep(2)} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">← Back</button>
                <div className="flex gap-3">
                  <button type="button" onClick={onClose} className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">Cancel</button>
                  <button type="submit" className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium shadow-sm">Create Survey</button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  const filteredSurveys = surveys.filter(survey => {
    const matchesSearch = survey.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      survey.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || survey.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="admin-container withdrawal-compact">
      {/* Page Header */}
      <div className="admin-section">
        <div className="admin-page-header">
          <div className="admin-header-content">
            <h1 className="text-base sm:text-lg font-bold text-gray-900">Survey Management</h1>
            <p className="text-xs text-gray-600">Create and manage surveys</p>
          </div>
          <div className="admin-header-actions">
            <button
              onClick={() => setShowCreateModal(true)}
              className="admin-button-primary w-full sm:w-auto justify-center sm:justify-start"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Survey
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search surveys by title or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-input search-input"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-input"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Surveys Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {loading ? (
          <div className="col-span-full">
            <SectionLoading message="Loading surveys..." />
          </div>
        ) : (
          filteredSurveys.map((survey) => (
            <div key={survey.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-4">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{survey.title}</h3>
                    <p className="text-xs text-gray-600 mb-2 line-clamp-2">{survey.description}</p>
                    {getStatusBadge(survey.status)}
                  </div>
                  <div className="relative ml-2">
                    <button
                      onClick={() => setActionDropdown(actionDropdown === survey.id ? null : survey.id)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>

                    {actionDropdown === survey.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
                        <button
                          onClick={() => {
                            handleViewDetails(survey.id);
                            setActionDropdown(null);
                          }}
                          className="flex items-center w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                        >
                          <Eye className="mr-2 h-3 w-3" />
                          View Details
                        </button>
                        <button
                          onClick={() => handleSurveyAction('duplicate', survey.id)}
                          className="flex items-center w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                        >
                          <Copy className="mr-2 h-3 w-3" />
                          Duplicate
                        </button>
                        {survey.status === 'draft' && (
                          <button
                            onClick={() => handleSurveyAction('activate', survey.id)}
                            className="flex items-center w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                          >
                            <Play className="mr-2 h-3 w-3" />
                            Activate
                          </button>
                        )}
                        {survey.status === 'active' && (
                          <button
                            onClick={() => handleSurveyAction('pause', survey.id)}
                            className="flex items-center w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-100"
                          >
                            <Pause className="mr-2 h-3 w-3" />
                            Pause
                          </button>
                        )}
                        <button
                          onClick={() => handleSurveyAction('delete', survey.id)}
                          className="flex items-center w-full px-3 py-1.5 text-xs text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="mr-2 h-3 w-3" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Category:</span>
                    <span className="font-medium">{survey.category}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Reward:</span>
                    <span className="font-medium text-green-600">${survey.reward}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600">Duration:</span>
                    <span className="font-medium">{survey.duration}</span>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">Progress:</span>
                      <span className="font-medium">{survey.responses}/{survey.targetResponses}</span>
                    </div>
                    {getProgressBar(survey.responses, survey.targetResponses)}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(survey.createdDate).toLocaleDateString()}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewDetails(survey.id)}
                      className="text-primary-600 hover:text-primary-800 text-xs font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Survey Details Modal */}
      {showSurveyModal && (
        <SurveyModal
          survey={selectedSurvey}
          onClose={() => {
            setShowSurveyModal(false);
            setSelectedSurvey(null);
          }}
        />
      )}

      {/* Create Survey Modal */}
      {showCreateModal && (
        <CreateSurveyModal
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
};

export default SurveyManagement;
